/**
 * EscalationsSection — persistent governance escalation inbox.
 *
 * Unlike AttentionSection (live WebSocket alerts that vanish on reload),
 * this section is backed by governance-service REST and shows every
 * pending escalation for the organization, with inline approve/deny.
 * Chat-ui counterpart of admin-ui's EscalationsPage.
 *
 * Cards are decision-centric: the title says WHAT wants to run (node label
 * from EscalationContextMetadata), context rows say where and under which
 * rule, and the raw rule-engine trace stays behind a details expander.
 * Expired escalations lose their Approve/Deny buttons — the producer's
 * 300s window has passed and resolving them can no longer affect the run.
 *
 * Polls every 30s while mounted and refreshes when the tab regains focus.
 *
 * A single-choice or free-text question is listed as a header only, under
 * the owner ruling of 2026-10-26 (Mikko Alasaarela; agion/Agion#3446,
 * challenge 2026-10-26-escalation-question-chat-card Mid-Eval 1): its
 * mission, that a question is waiting, and the time. The header opens the
 * run's chat thread, where the in-thread card answers it; a run this app has
 * no address for opens in the admin console. Only open questions are
 * listed, newest first. Approvals keep their controls here, since not every
 * approval has an in-thread card that decides it.
 *
 * A form question (agion/Agion#3427) renders from its stored
 * question only: one choice per offered option, or a text answer, sent to
 * the answer endpoint. It has no Approve or Deny. Once answered it shows the
 * recorded answer and, when governance names them, who answered. Governance
 * validates every answer; a refusal shows in the card.
 *
 * A free-text answer goes to the run, never to governance (owner ruling
 * 2026-10-25, agion/Agion#3427 and agion/Agion#3437; the escalation store owns
 * the send). Once answered the card shows who answered and when and that the
 * answer is kept with the run; a row answered before the ruling shows its text.
 *
 * A form question (PLAN-20261001 slice 20, agion/Agion#3437) renders its
 * heading and each text, number and date field from the stored question,
 * with no value, placeholder or hint of the browser's own. Its answer goes to
 * the run like a free text: the filled fields only, a number as a number, {}
 * when none could be determined. Once answered it shows who answered, when
 * and which fields, never a value. When the escalation names its evidence,
 * Open evidence shows it read-only beside the form, opened as the viewer.
 *
 * With operator takeover on (agion/Agion#3435), a pending chat handoff that
 * names its run offers Take over instead of Approve and Deny: it accepts the
 * hold and opens the run's conversation in operator mode. A refusal shows in
 * the card by its code. Each hold the viewer holds offers Open conversation.
 */

import React, { useCallback, useEffect, useId, useMemo, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { motion, AnimatePresence } from 'framer-motion'
import { ShieldCheck, ShieldX, ChevronDown, ChevronRight, ExternalLink, Headset, MessageSquare } from 'lucide-react'
import { Badge, Button, Input, Label, RadioGroup, RadioGroupItem, Text, Textarea, cn } from '@agion/ui'
import { isOperatorHold, useEscalationStore, usePendingEscalations } from '../../hooks/useEscalations'
import { useHeldConversations, useStartedByViewer, useTakeOver } from '../../hooks/useOperatorTakeover'
import { operatorConversationHref, takeOverRefusalText } from '../../services/operator-takeover'
import { operatorTakeoverEnabled } from '../../utils/config'
import { useMissionStore } from '../../hooks/useMissions'
import { useCountdown } from '../../hooks/useCountdown'
import { useEscalationEvidence } from '../../hooks/useEscalationEvidence'
import { ESCALATIONS_SECTION_ID, useEscalationsSectionAnchor } from '../../hooks/useEscalationsReveal'
import {
  adminEscalationsHref,
  answeredInThread,
  escalationMissionId,
  escalationThreadTarget,
  offersAnswerInEscalations,
  openQuestionHeaders,
} from '../../services/escalation-thread'
import {
  answerKeptWithRun,
  answerRefusalText,
  answeredAtText,
  recordedAnswerText,
} from '../../services/escalation-answer'
import {
  answerableFormFields,
  answeredFieldLabels,
  evidenceRefOf,
  evidenceSectionLabel,
  formFieldInvalid,
  formValuesOf,
  type EvidenceRef,
  type EvidenceView,
  type FormEntries,
} from '../../services/escalation-form'
import { evidenceRefusalText } from '../../services/run-output-open'
import type {
  ChoiceOrTextQuestion,
  Escalation,
  EscalationContextMetadata,
  EscalationQuestion,
  EscalationQuestionFormField,
} from '../../types/escalations'

const POLL_INTERVAL_MS = 30_000
const URGENT_THRESHOLD_MS = 60_000

type Translate = (key: string, fallback: string) => string

/** The longest text a one-line input takes; a longer or unbounded text field gets a textarea. */
const ONE_LINE_TEXT_MAX = 200

/** The message under a field whose entry the browser cannot send. */
function fieldInvalidText(field: EscalationQuestionFormField, t: Translate): string {
  return field.kind === 'date'
    ? t('escalations.question.form.error.date', 'Enter a complete date.')
    : t('escalations.question.form.error.number', 'Enter a number.')
}

/** A form question's fields, in order, and its answer action. */
function EscalationFormBody({
  escalation,
  fields,
  isAnswering,
  labelledBy,
}: {
  escalation: Escalation
  fields: EscalationQuestionFormField[]
  isAnswering: boolean
  labelledBy: string
}) {
  const { t } = useTranslation('chat')
  const tr = t as unknown as Translate
  const answerEscalation = useEscalationStore((s) => s.answerEscalation)
  const idBase = useId()
  const [entries, setEntries] = useState<FormEntries>({})

  const values = formValuesOf(fields, entries)
  const canSubmit = !isAnswering && values !== undefined
  const noneFilled = values !== undefined && Object.keys(values).length === 0

  // Read on input as well as change: a number or date input the browser
  // cannot read keeps an empty value, so only its validity says so.
  const update = (id: string) => (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = event.currentTarget
    const badInput = target.validity?.badInput === true
    setEntries((current) => ({ ...current, [id]: { value: target.value, badInput } }))
  }

  const submit = () => {
    if (!canSubmit || !values) return
    void answerEscalation(escalation.id, { form_values: values })
  }

  return (
    <div role="group" aria-labelledby={labelledBy} className="space-y-2">
      {fields.map((field) => {
        const inputId = `${idBase}-${field.id}`
        const entry = entries[field.id]
        const invalid = formFieldInvalid(field, entry)
        const hintId = field.description ? `${inputId}-hint` : undefined
        const errorId = invalid ? `${inputId}-error` : undefined
        const common = {
          id: inputId,
          value: entry?.value ?? '',
          onChange: update(field.id),
          onInput: update(field.id),
          disabled: isAnswering,
          'aria-required': field.required === true,
          'aria-invalid': invalid,
          'aria-describedby': [hintId, errorId].filter(Boolean).join(' ') || undefined,
          className: 'text-xs',
        }
        return (
          <div key={field.id} className="space-y-1" data-testid="escalation-form-field" data-field-kind={field.kind}>
            <Label htmlFor={inputId} className="block text-xs">
              {field.label}
              {field.required === true && (
                <span data-testid="escalation-form-required">
                  {' '}
                  <span aria-hidden="true">*</span>
                  <span className="sr-only">{t('escalations.question.form.required', 'Required')}</span>
                </span>
              )}
            </Label>
            {field.description && (
              <Text size="xs" color="muted" id={hintId} className="block break-words">
                {field.description}
              </Text>
            )}
            {field.kind === 'number' ? (
              <Input {...common} type="number" step="any" inputMode="decimal" min={field.min} max={field.max} />
            ) : field.kind === 'date' ? (
              <Input {...common} type="date" />
            ) : field.max_length !== undefined && field.max_length <= ONE_LINE_TEXT_MAX ? (
              <Input {...common} type="text" maxLength={field.max_length} />
            ) : (
              <Textarea {...common} rows={2} maxLength={field.max_length} />
            )}
            {invalid && (
              <Text
                size="xs"
                id={errorId}
                className="block break-words text-red-600 dark:text-red-400"
                data-testid="escalation-form-field-error"
              >
                {fieldInvalidText(field, tr)}
              </Text>
            )}
          </div>
        )
      })}
      <Button
        variant="primary"
        size="sm"
        onClick={submit}
        disabled={!canSubmit}
        data-testid="escalation-answer-submit"
        className="w-full"
      >
        {isAnswering
          ? t('escalations.resolving', 'Submitting…')
          : noneFilled
            ? t('escalations.question.form.submitNone', 'I could not determine any of these')
            : t('escalations.question.submit', 'Send answer')}
      </Button>
    </div>
  )
}

/**
 * Opened evidence, read-only: one labelled section per text value, or the
 * text as it came. Shared by this section and the current chat's in-thread
 * question card (agion/Agion#3446).
 */
export function EscalationEvidenceContent({ view }: { view: EvidenceView }) {
  const { t } = useTranslation('chat')
  const tr = t as unknown as Translate
  return view.kind === 'sections' ? (
    <div
      role="region"
      aria-label={t('escalations.evidence.label', 'Evidence')}
      className="max-h-64 space-y-2 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-700/30"
      data-testid="escalation-evidence-content"
    >
      {view.sections.map((section) => (
        <div key={section.key} className="space-y-0.5" data-testid="escalation-evidence-section">
          <Text size="xs" color="muted" className="block font-medium" data-testid="escalation-evidence-section-label">
            {evidenceSectionLabel(section.key, tr)}
          </Text>
          <pre className="whitespace-pre-wrap break-words font-mono text-xs" data-testid="escalation-evidence-section-text">
            {section.text}
          </pre>
        </div>
      ))}
    </div>
  ) : (
    <pre
      role="region"
      aria-label={t('escalations.evidence.label', 'Evidence')}
      className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md border border-slate-200 bg-slate-50 p-2 font-mono text-xs dark:border-slate-700 dark:bg-slate-700/30"
      data-testid="escalation-evidence-content"
    >
      {view.text}
    </pre>
  )
}

/** Open evidence, and the evidence read-only once opened, or why it did not open. */
function EscalationEvidence({ evidence }: { evidence: EvidenceRef }) {
  const { t } = useTranslation('chat')
  const tr = t as unknown as Translate
  const { state, open, close } = useEscalationEvidence(evidence)
  return (
    <div className="space-y-1.5">
      <Button
        variant="outline"
        size="sm"
        onClick={state.status === 'open' ? close : () => void open()}
        disabled={state.status === 'opening'}
        aria-expanded={state.status === 'open'}
        data-testid="escalation-evidence-open"
        className="w-full"
      >
        {state.status === 'opening'
          ? t('escalations.evidence.opening', 'Opening…')
          : state.status === 'open'
            ? t('escalations.evidence.hide', 'Hide evidence')
            : t('escalations.evidence.open', 'Open evidence')}
      </Button>
      {state.status === 'open' && <EscalationEvidenceContent view={state.view} />}
      {state.status === 'refused' && (
        <Text
          size="xs"
          role="alert"
          className="block break-words text-red-600 dark:text-red-400"
          data-testid="escalation-evidence-error"
        >
          {evidenceRefusalText(state.refusal, tr)}
        </Text>
      )}
    </div>
  )
}

/**
 * The pending escalations plus the question escalations answered since the
 * last poll, so an answer stays visible in its card. Without an answered
 * question the pending list is returned as it is.
 */
function withAnsweredQuestions(pending: Escalation[], stored: Escalation[] | undefined): Escalation[] {
  const answered = (stored ?? []).filter((e) => e.status === 'answered' && e.question)
  if (answered.length === 0) return pending
  return [...pending, ...answered].sort((a, b) => Date.parse(a.expires_at) - Date.parse(b.expires_at))
}

/**
 * The answer of a pending single-choice or free-text question: its choices or
 * its text box and Send answer, through the escalation store. Shared by this
 * section and the current chat's in-thread question card (agion/Agion#3446);
 * touchTargets gives the choices and the button 44px rows for phones.
 */
export function EscalationQuestionAnswer({
  escalationId,
  question,
  labelledBy,
  touchTargets = false,
}: {
  escalationId: string
  question: ChoiceOrTextQuestion
  labelledBy: string
  touchTargets?: boolean
}) {
  const { t } = useTranslation('chat')
  const answerEscalation = useEscalationStore((s) => s.answerEscalation)
  const isAnswering = useEscalationStore((s) => s.answeringIds?.has(escalationId) ?? false)
  const idBase = useId()
  const [selected, setSelected] = useState('')
  const [text, setText] = useState('')
  const canSubmit = !isAnswering && (question.type === 'single_choice' ? selected !== '' : text.trim() !== '')

  const submit = () => {
    if (!canSubmit) return
    void answerEscalation(
      escalationId,
      question.type === 'single_choice' ? { selected_option_ids: [selected] } : { text },
    )
  }

  return (
    <>
      {question.type === 'single_choice' ? (
        <RadioGroup
          value={selected}
          onValueChange={setSelected}
          aria-labelledby={labelledBy}
          disabled={isAnswering}
          className="gap-1.5"
        >
          {(question.options ?? []).map((option, index) => {
            const optionId = `${idBase}-option-${index}`
            const descriptionId = option.description ? `${optionId}-description` : undefined
            return (
              <div
                key={option.id}
                className={cn('flex items-start gap-2', touchTargets && 'min-h-[44px] items-center')}
                data-testid="escalation-answer-option"
              >
                <RadioGroupItem value={option.id} id={optionId} aria-describedby={descriptionId} className={touchTargets ? undefined : 'mt-0.5'} />
                <div className="min-w-0 flex-1">
                  <Label htmlFor={optionId} className={cn('cursor-pointer text-sm', touchTargets && 'flex min-h-[44px] items-center')}>
                    {option.label}
                  </Label>
                  {option.description && (
                    <Text size="xs" color="muted" id={descriptionId} className="block break-words">
                      {option.description}
                    </Text>
                  )}
                </div>
              </div>
            )
          })}
        </RadioGroup>
      ) : (
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={question.max_length}
          placeholder={t('escalations.question.answerPlaceholder', 'Your answer')}
          aria-labelledby={labelledBy}
          rows={3}
          className="text-xs"
          disabled={isAnswering}
          data-testid="escalation-answer-text"
        />
      )}
      <Button
        variant="primary"
        size="sm"
        onClick={submit}
        disabled={!canSubmit}
        data-testid="escalation-answer-submit"
        className={cn('w-full', touchTargets && 'min-h-[44px]')}
      >
        {isAnswering
          ? t('escalations.resolving', 'Submitting…')
          : t('escalations.question.submit', 'Send answer')}
      </Button>
    </>
  )
}

/** Governance's refusal of the last answer to this question, when there is one. */
export function EscalationAnswerRefusal({ escalationId }: { escalationId: string }) {
  const { t } = useTranslation('chat')
  const tr = t as unknown as Translate
  const refusal = useEscalationStore((s) => s.answerRefusals?.[escalationId])
  if (!refusal) return null
  return (
    <Text
      size="xs"
      role="alert"
      className="block break-words text-red-600 dark:text-red-400"
      data-testid="escalation-answer-error"
    >
      {answerRefusalText(refusal, tr)}
    </Text>
  )
}

/** The question, its answer form, or the recorded answer. */
function EscalationQuestionBody({
  escalation,
  question,
  isExpired,
}: {
  escalation: Escalation
  question: EscalationQuestion
  isExpired: boolean
}) {
  const { t } = useTranslation('chat')
  const isAnswering = useEscalationStore((s) => s.answeringIds?.has(escalation.id) ?? false)
  const idBase = useId()

  const isAnswered = escalation.status === 'answered'
  const answerText = isAnswered ? recordedAnswerText(question, escalation.answer) : undefined
  const keptWithRun = answerKeptWithRun(escalation, question)
  const answeredAt = keptWithRun ? answeredAtText(escalation.answered_at) : undefined
  const answeredFields = isAnswered && question.type === 'form' ? answeredFieldLabels(question, escalation.answer) : undefined
  const formFields = answerableFormFields(question)
  const evidence = useMemo(() => evidenceRefOf(escalation), [escalation])

  return (
    <div className="mt-2 space-y-1.5" data-testid="escalation-question">
      <Text size="sm" className="block break-words" id={`${idBase}-text`}>
        {question.text}
      </Text>

      {evidence && <EscalationEvidence evidence={evidence} />}

      {isAnswered ? (
        <div className="space-y-0.5">
          {answerText && (
            <Text size="xs" className="block break-words" data-testid="escalation-answer">
              {t('escalations.question.answerLabel', 'Answer')}: {answerText}
            </Text>
          )}
          {keptWithRun && (
            <Text size="xs" className="block break-words" data-testid="escalation-answer-kept">
              {t('escalations.question.answerKeptWithRun', 'The answer is kept with the run.')}
            </Text>
          )}
          {answeredFields &&
            (answeredFields.length > 0 ? (
              <Text size="xs" className="block break-words" data-testid="escalation-form-answered-fields">
                {t('escalations.question.form.answeredFields', 'Fields answered')}: {answeredFields.join(', ')}
              </Text>
            ) : (
              <Text size="xs" className="block break-words" data-testid="escalation-form-answered-none">
                {t('escalations.question.form.noneAnswered', 'No field could be determined.')}
              </Text>
            ))}
          {escalation.answered_by_name && (
            <Text size="xs" color="muted" className="block truncate" data-testid="escalation-answered-by">
              {t('escalations.question.answeredByLabel', 'Answered by')}: {escalation.answered_by_name}
            </Text>
          )}
          {answeredAt && (
            <Text size="xs" color="muted" className="block truncate" data-testid="escalation-answered-at">
              {t('escalations.question.answeredAtLabel', 'Answered at')}: {answeredAt}
            </Text>
          )}
        </div>
      ) : isExpired ? (
        <Text size="xs" color="muted" className="block italic">
          {t('escalations.expiredNote', 'No longer actionable — the step timed out before a decision.')}
        </Text>
      ) : formFields ? (
        <EscalationFormBody
          escalation={escalation}
          fields={formFields}
          isAnswering={isAnswering}
          labelledBy={`${idBase}-text`}
        />
      ) : null}

      <EscalationAnswerRefusal escalationId={escalation.id} />
    </div>
  )
}

/** Take over of a pending hold, and governance's refusal of it. */
function TakeOverAction({ escalation }: { escalation: Escalation }) {
  const { t } = useTranslation('chat')
  const tr = t as unknown as Translate
  const takeOver = useTakeOver()
  const isAccepting = useEscalationStore((s) => s.acceptingIds?.has(escalation.id) ?? false)
  const refusal = useEscalationStore((s) => s.acceptRefusals?.[escalation.id])
  return (
    <div className="mt-2 space-y-1">
      <Button
        variant="primary"
        size="sm"
        onClick={() => void takeOver(escalation)}
        disabled={isAccepting}
        data-testid="escalation-take-over"
        className="w-full"
      >
        <Headset className="w-3.5 h-3.5" />
        {isAccepting
          ? t('operatorTakeover.takingOver', 'Taking over…')
          : t('operatorTakeover.takeOver', 'Take over')}
      </Button>
      {refusal && (
        <Text
          size="xs"
          role="alert"
          className="block break-words text-red-600 dark:text-red-400"
          data-testid="escalation-take-over-error"
        >
          {takeOverRefusalText(tr, refusal)}
        </Text>
      )}
    </div>
  )
}

/** Take over, or for the run's starter as governance names them, why they cannot. */
function TakeOverOrStarterNote({ escalation }: { escalation: Escalation }) {
  const { t } = useTranslation('chat')
  const startedByViewer = useStartedByViewer(escalation)
  if (!startedByViewer) return <TakeOverAction escalation={escalation} />
  return (
    <Text size="xs" color="muted" className="mt-2 block italic" data-testid="escalation-take-over-self">
      {takeOverRefusalText(t as unknown as Translate, 'self')}
    </Text>
  )
}

/** A hold the viewer holds: its mission and the way back into its conversation. */
function HeldConversationItem({ hold }: { hold: Escalation }) {
  const { t } = useTranslation('chat')
  const getMissionById = useMissionStore((s) => s.getMissionById)
  const missionName = hold.mission_id ? getMissionById(hold.mission_id)?.name : undefined
  return (
    <div
      className="px-3 py-2 rounded-lg bg-white/60 dark:bg-slate-800/40 border border-slate-200/60 dark:border-slate-700/40"
      data-testid="escalation-held-conversation"
    >
      <Text size="sm" weight="medium" className="block truncate">
        {missionName ?? t('escalations.type.chatHandoff', 'Handoff requested')}
      </Text>
      <Text size="xs" color="muted" className="block">
        {t('operatorTakeover.heldByYou', 'You are holding this conversation.')}
      </Text>
      {hold.mission_id && hold.execution_id && (
        <Button variant="secondary" size="sm" asChild className="mt-2 w-full">
          <Link
            to={operatorConversationHref(hold.mission_id, hold.execution_id, hold.id)}
            data-testid="escalation-open-conversation"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {t('operatorTakeover.openConversation', 'Open conversation')}
          </Link>
        </Button>
      )}
    </div>
  )
}

/**
 * A question listed as a header (agion/Agion#3446, owner ruling of
 * 2026-10-26): the mission, that a question is waiting, and the time. It
 * opens the run's chat thread, or the admin console when this app has no
 * address for the run; it offers no answer. A small Answer in Escalations
 * link opens the escalation in the admin console's Escalations, for a run
 * with no in-thread card. Gone once the question expires.
 */
function EscalationQuestionHeader({ escalation }: { escalation: Escalation }) {
  const { t } = useTranslation('chat')
  const getMissionById = useMissionStore((s) => s.getMissionById)
  const loadMissionDetails = useMissionStore((s) => s.loadMissionDetails)
  const missionId = escalationMissionId(escalation)
  const mission = missionId ? getMissionById(missionId) : undefined

  useEffect(() => {
    if (missionId && !mission) void loadMissionDetails(missionId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId])

  const expiresAtMs = Date.parse(escalation.expires_at)
  const countdown = useCountdown(0, Number.isFinite(expiresAtMs) ? expiresAtMs : undefined)
  const isExpired = Number.isFinite(expiresAtMs) && expiresAtMs - Date.now() <= 0
  const isUrgent = !isExpired && Number.isFinite(expiresAtMs) && expiresAtMs - Date.now() < URGENT_THRESHOLD_MS
  if (isExpired) return null

  const target = escalationThreadTarget(escalation)
  const className = 'block px-3 py-2 rounded-t-lg hover:bg-white dark:hover:bg-slate-800/70'
  const body = (
    <>
      <span className="flex items-center gap-2">
        <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', isUrgent ? 'bg-red-500 animate-pulse' : 'bg-amber-500 animate-pulse')} />
        <Text size="sm" weight="medium" className="truncate flex-1 min-w-0" data-testid="escalation-header-mission">
          {mission?.name ?? t('escalations.header.unknownMission', 'A mission')}
        </Text>
        {countdown && (
          <span
            className={cn(
              'text-xs font-mono flex-shrink-0',
              isUrgent ? 'text-red-500 dark:text-red-400' : 'text-amber-600 dark:text-amber-400',
            )}
            title={Number.isFinite(expiresAtMs) ? new Date(expiresAtMs).toLocaleTimeString() : undefined}
            data-testid="escalation-countdown"
          >
            {countdown}
          </span>
        )}
      </span>
      <Text size="xs" color="muted" className="mt-0.5 flex items-center gap-1" data-testid="escalation-header-status">
        <MessageSquare className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
        {t('escalations.header.questionWaiting', 'A question is waiting')}
      </Text>
    </>
  )
  return (
    <div className="rounded-lg bg-white/60 dark:bg-slate-800/40 border border-slate-200/60 dark:border-slate-700/40">
      {target.kind === 'thread' ? (
        <Link to={target.to} className={className} data-testid="escalation-question-header" data-escalation-id={escalation.id}>
          {body}
        </Link>
      ) : (
        <a
          href={target.href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          data-testid="escalation-question-header"
          data-escalation-id={escalation.id}
        >
          {body}
        </a>
      )}
      {offersAnswerInEscalations(escalation) && (
        <a
          href={adminEscalationsHref(escalation.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 px-3 pb-2 text-xs text-blue-600 underline-offset-2 hover:underline dark:text-blue-400"
          data-testid="escalation-header-answer-link"
        >
          {t('escalations.header.answerInEscalations', 'Answer in Escalations')}
          <ExternalLink className="w-3 h-3" aria-hidden="true" />
        </a>
      )}
    </div>
  )
}

/** Humanize the raw escalation_type enum as a last-resort title. */
function typeFallbackTitle(escalationType: string | undefined, t: (key: string, fallback: string) => string): string {
  switch (escalationType) {
    case 'action_approval':
      return t('escalations.type.actionApproval', 'Approval required')
    case 'chat_handoff':
      return t('escalations.type.chatHandoff', 'Handoff requested')
    case 'content_review':
      return t('escalations.type.contentReview', 'Content review')
    case 'guidance':
      return t('escalations.type.guidance', 'Guidance requested')
    case 'compliance_review':
      return t('escalations.type.complianceReview', 'Compliance review')
    case 'question':
      return t('escalations.type.question', 'Question')
    default:
      return t('escalations.fallbackTitle', 'Escalation')
  }
}

function EscalationItem({ escalation }: { escalation: Escalation }) {
  const { t } = useTranslation('chat')
  const resolveEscalation = useEscalationStore((s) => s.resolveEscalation)
  const resolvingIds = useEscalationStore((s) => s.resolvingIds)
  const getMissionById = useMissionStore((s) => s.getMissionById)
  const loadMissionDetails = useMissionStore((s) => s.loadMissionDetails)

  const [pendingDecision, setPendingDecision] = useState<'approved' | 'denied' | null>(null)
  const [reason, setReason] = useState('')
  const [showTrace, setShowTrace] = useState(false)

  const expiresAtMs = Date.parse(escalation.expires_at)
  // useCountdown measures elapsed-since-receivedAt against timeoutMs;
  // anchoring at 0 makes it count down to the absolute expiry time.
  // It also re-renders this item every second, keeping isExpired live.
  const countdown = useCountdown(0, Number.isFinite(expiresAtMs) ? expiresAtMs : undefined)
  const isExpired = Number.isFinite(expiresAtMs) && expiresAtMs - Date.now() <= 0
  const isUrgent = !isExpired && Number.isFinite(expiresAtMs) && expiresAtMs - Date.now() < URGENT_THRESHOLD_MS

  const md: EscalationContextMetadata = escalation.metadata ?? {}
  const mission = escalation.mission_id ? getMissionById(escalation.mission_id) : undefined
  const missionName = mission?.name

  // Mission not in the recent-missions store (the common case for older
  // missions) — fetch it once so the card can name the mission instead of
  // showing a raw enum. loadMissionDetails inserts into the store, so the
  // re-render picks it up via getMissionById.
  useEffect(() => {
    if (escalation.mission_id && !mission) {
      void loadMissionDetails(escalation.mission_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escalation.mission_id])

  const title = md.node_label ?? missionName ?? typeFallbackTitle(escalation.escalation_type, t)
  const actionTarget = md.tool_name ?? md.resource_name
  // Old escalations carry the trace in `reason`; new ones duplicate it into
  // metadata.trace_reason while `reason` stays human-readable. Only offer
  // the expander when the trace adds information over the visible reason.
  const trace = md.trace_reason && md.trace_reason !== escalation.reason ? md.trace_reason : undefined

  const isResolving = resolvingIds.has(escalation.id)
  const isQuestion = escalation.escalation_type === 'question'
  const question = isQuestion ? escalation.question : undefined
  const isAnswered = escalation.status === 'answered'
  const offersTakeOver = operatorTakeoverEnabled() && isOperatorHold(escalation) && escalation.status === 'pending'

  const handleConfirm = useCallback(async () => {
    if (!pendingDecision) return
    try {
      await resolveEscalation(escalation.id, pendingDecision, reason.trim())
      setPendingDecision(null)
      setReason('')
    } catch {
      // Error is surfaced via the store's error state; keep the form open
    }
  }, [escalation.id, pendingDecision, reason, resolveEscalation])

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      className="px-3 py-2 rounded-lg bg-white/60 dark:bg-slate-800/40 border border-slate-200/60 dark:border-slate-700/40"
      data-testid="escalation-item"
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'w-1.5 h-1.5 rounded-full flex-shrink-0',
            isExpired
              ? 'bg-slate-400'
              : isUrgent
                ? 'bg-red-500 animate-pulse'
                : 'bg-amber-500 animate-pulse',
          )}
        />
        <Text size="sm" weight="medium" className="truncate flex-1 min-w-0">
          {title}
        </Text>
        {isAnswered ? null : isExpired ? (
          <span
            className="text-xs flex-shrink-0 text-slate-400 dark:text-slate-500"
            data-testid="escalation-expired"
          >
            {t('escalations.expired', 'Expired')}
          </span>
        ) : (
          countdown && (
            <span
              className={cn(
                'text-xs font-mono flex-shrink-0',
                isUrgent ? 'text-red-500 dark:text-red-400' : 'text-amber-600 dark:text-amber-400',
              )}
              title={Number.isFinite(expiresAtMs) ? new Date(expiresAtMs).toLocaleTimeString() : undefined}
            >
              {countdown}
            </span>
          )
        )}
      </div>

      {/* Context rows: where this is happening and which rule stopped it */}
      <div className="mt-1 space-y-0.5">
        {escalation.mission_id && (
          <Text size="xs" color="muted" className="block truncate" data-testid="escalation-mission">
            {t('escalations.missionLabel', 'Mission')}:{' '}
            <Link
              to={`/mission/${escalation.mission_id}`}
              className="underline decoration-dotted underline-offset-2 hover:text-slate-700 dark:hover:text-slate-300"
            >
              {missionName ?? escalation.mission_id.slice(0, 8)}
            </Link>
          </Text>
        )}
        {actionTarget && (
          <Text size="xs" color="muted" className="block truncate" data-testid="escalation-target">
            {t('escalations.actionLabel', 'Wants to run')}: {actionTarget}
            {md.resource_operation ? ` (${md.resource_operation})` : ''}
          </Text>
        )}
        {md.rule_name && (
          <Text size="xs" color="muted" className="block truncate" data-testid="escalation-rule">
            {t('escalations.ruleLabel', 'Rule')}: {md.rule_name}
          </Text>
        )}
      </div>

      <Text size="xs" color="muted" className="mt-1 block break-words">
        {escalation.reason}
      </Text>

      {trace && (
        <div className="mt-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowTrace((v) => !v)}
            className="h-auto px-0 py-0 text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
            data-testid="escalation-trace-toggle"
          >
            {showTrace ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {t('escalations.traceToggle', 'Policy details')}
          </Button>
          {showTrace && (
            <Text
              size="xs"
              color="muted"
              className="mt-0.5 block break-words font-mono"
              data-testid="escalation-trace"
            >
              {trace}
            </Text>
          )}
        </div>
      )}

      {question ? (
        <EscalationQuestionBody escalation={escalation} question={question} isExpired={isExpired} />
      ) : isQuestion ? (
        <Text size="xs" color="muted" className="mt-2 block italic" data-testid="escalation-question-unavailable">
          {t('escalations.question.unavailable', 'This question cannot be shown.')}
        </Text>
      ) : isExpired ? (
        <Text size="xs" color="muted" className="mt-2 block italic">
          {t('escalations.expiredNote', 'No longer actionable — the step timed out before a decision.')}
        </Text>
      ) : offersTakeOver ? (
        <TakeOverOrStarterNote escalation={escalation} />
      ) : pendingDecision === null ? (
        <>
          <div className="flex gap-1.5 mt-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPendingDecision('approved')}
              disabled={isResolving}
              data-testid="escalation-approve"
              className="flex-1"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              {t('escalations.approve', 'Approve')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPendingDecision('denied')}
              disabled={isResolving}
              data-testid="escalation-deny"
              className="flex-1"
            >
              <ShieldX className="w-3.5 h-3.5" />
              {t('escalations.deny', 'Deny')}
            </Button>
          </div>
          <Text size="xs" color="muted" className="mt-1 block">
            {t('escalations.consequence', 'Approve runs this step now · Deny blocks it')}
          </Text>
        </>
      ) : (
        <div className="mt-2 space-y-1.5">
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('escalations.reasonPlaceholder', 'Reason (optional)')}
            rows={2}
            className="text-xs"
            data-testid="escalation-reason"
          />
          <div className="flex gap-1.5">
            <Button
              variant={pendingDecision === 'approved' ? 'primary' : 'destructive'}
              size="sm"
              onClick={handleConfirm}
              disabled={isResolving}
              data-testid="escalation-confirm"
              className="flex-1"
            >
              {isResolving
                ? t('escalations.resolving', 'Submitting…')
                : pendingDecision === 'approved'
                  ? t('escalations.confirmApprove', 'Confirm approval')
                  : t('escalations.confirmDeny', 'Confirm denial')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPendingDecision(null)
                setReason('')
              }}
              disabled={isResolving}
            >
              {t('escalations.cancel', 'Cancel')}
            </Button>
          </div>
        </div>
      )}
    </motion.div>
  )
}

export function EscalationsSection({ searchTerm = '' }: { searchTerm?: string }) {
  const { t } = useTranslation('chat')
  const pending = usePendingEscalations()
  const stored = useEscalationStore((s) => s.escalations)
  const loadEscalations = useEscalationStore((s) => s.loadEscalations)
  const inbox = useMemo(() => withAnsweredQuestions(pending, stored), [pending, stored])
  const held = useHeldConversations()
  useEscalationsSectionAnchor()

  // Poll while mounted + refresh when the tab regains visibility
  useEffect(() => {
    const load = () => void loadEscalations({ status: 'pending', limit: 20 })
    load()
    const interval = setInterval(load, POLL_INTERVAL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') load()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [loadEscalations])

  const filtered = searchTerm
    ? inbox.filter((e) => {
        const term = searchTerm.toLowerCase()
        const md: EscalationContextMetadata = e.metadata ?? {}
        return (
          e.reason?.toLowerCase().includes(term) ||
          e.escalation_type?.toLowerCase().includes(term) ||
          md.node_label?.toLowerCase().includes(term) ||
          md.tool_name?.toLowerCase().includes(term) ||
          md.rule_name?.toLowerCase().includes(term)
        )
      })
    : inbox

  // Questions the in-thread card answers are headers, open ones only and
  // newest first; every other escalation keeps its item.
  const headers = openQuestionHeaders(filtered)
  const items = filtered.filter((e) => !answeredInThread(e))

  if (items.length === 0 && headers.length === 0 && held.length === 0) return null

  return (
    <div id={ESCALATIONS_SECTION_ID} className="mb-3" data-testid="escalations-section">
      <div className="flex items-center gap-2 px-1 py-2">
        <span className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider flex-1">
          {t('escalations.title', 'Escalations')}
        </span>
        <Badge variant="destructive" size="sm">
          {items.filter((e) => e.status === 'pending').length + headers.length}
        </Badge>
      </div>

      <AnimatePresence>
        <div className="space-y-1.5">
          {held.map((hold) => (
            <HeldConversationItem key={`held-${hold.id}`} hold={hold} />
          ))}
          {headers.map((escalation) => (
            <EscalationQuestionHeader key={escalation.id} escalation={escalation} />
          ))}
          {items.map((escalation) => (
            <EscalationItem key={escalation.id} escalation={escalation} />
          ))}
        </div>
      </AnimatePresence>
    </div>
  )
}
